<template>
  <div class="array-field">
    <div class="flex items-center justify-between mb-3">
      <label class="block text-sm font-medium text-gray-700">{{ label }}</label>
      <Button
        icon="pi pi-plus"
        size="small"
        @click="addItem"
        v-tooltip="'Add item'"
        class="text-xs"
      />
    </div>

    <!-- Empty state -->
    <div v-if="items.length === 0" class="text-center py-4 text-gray-500 text-sm border border-dashed border-gray-300 rounded">
      No items yet. Click the + button to add one.
    </div>

    <!-- Array items -->
    <div v-else class="space-y-2">
      <div
        v-for="(item, index) in items"
        :key="item.id || index"
        class="border border-gray-200 rounded-lg p-3 bg-gray-50"
      >
        <div class="flex items-start justify-between mb-2">
          <div class="flex items-center space-x-2">
            <span class="text-xs font-medium text-gray-600">#{{ index + 1 }}</span>
            <Button
              v-if="index > 0"
              icon="pi pi-angle-up"
              size="small"
              text
              @click="moveItem(index, -1)"
              v-tooltip="'Move up'"
              class="text-xs"
            />
            <Button
              v-if="index < items.length - 1"
              icon="pi pi-angle-down"
              size="small"
              text
              @click="moveItem(index, 1)"
              v-tooltip="'Move down'"
              class="text-xs"
            />
          </div>
          <Button
            icon="pi pi-trash"
            size="small"
            text
            severity="danger"
            @click="removeItem(index)"
            v-tooltip="'Remove item'"
            class="text-xs"
          />
        </div>

        <!-- Array item editor -->
        <ArrayItemEditor
          :item="item"
          :item-schema="itemSchema"
          :project-id="projectId"
          @update="updateItem(index, $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Button from 'primevue/button'
import ArrayItemEditor from './ArrayItemEditor.vue'
import { emptyValueForField, moveItem as moveListItem } from '@/utils/editorHelpers'

interface Props {
  modelValue: any[]
  label: string
  itemSchema: Record<string, any>
  projectId: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: any[]]
}>()

// Reactive array items
const items = computed({
  get: () => props.modelValue || [],
  set: (value) => emit('update:modelValue', value)
})

// Add new item
const addItem = () => {
  const newItem = createEmptyItem()
  const newItems = [...items.value, newItem]
  items.value = newItems
}

// Remove item
const removeItem = (index: number) => {
  const newItems = items.value.filter((_, i) => i !== index)
  items.value = newItems
}

// Move item up/down
const moveItem = (index: number, direction: number) => {
  const newIndex = index + direction
  if (newIndex < 0 || newIndex >= items.value.length) return
  items.value = moveListItem(items.value, index, newIndex)
}

// Update specific item
const updateItem = (index: number, updatedItem: any) => {
  const newItems = [...items.value]
  newItems[index] = { ...newItems[index], ...updatedItem }
  items.value = newItems
}

// Create empty item based on schema
const createEmptyItem = () => {
  const emptyItem: any = {
    id: Date.now() + Math.random() // Simple ID generation
  }

  // Initialize fields based on schema
  Object.entries(props.itemSchema).forEach(([key, fieldDef]: [string, any]) => {
    emptyItem[key] = emptyValueForField(fieldDef)
  })

  return emptyItem
}
</script>